import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IPagedCollection } from '../../extViewmodels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { ISupportThread } from '../../models/Support/ISupportThread';
import { ThreadStatus, ThreadType } from '../../models/Support/SupportEnums';
import { invalidateTicketThreads, SUPPORT_PATHS, SUPPORT_QUERY_KEYS } from './supportQueryHelpers';

type ThreadsQueryData = IPagedCollection<ISupportThread>;

interface AddThreadParams {
	type: ThreadType;
	body: string;
	attachmentIds?: string[];
	mentionedUserIds?: string[];
}

export function useAddThreadMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: AddThreadParams) =>
			userSession.webServiceHelper.callAsync<ISupportThread>(SUPPORT_PATHS.threads.list(ticketId), 'POST', params),
		onSuccess: () => invalidateTicketThreads(queryClient, ticketId),
	});
}

export function useUpdateThreadStatusMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const threadsKey = SUPPORT_QUERY_KEYS.tickets.threads(ticketId);

	return useMutation({
		mutationFn: ({ threadId, status }: { threadId: string; status: ThreadStatus | string }) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.threads.status(ticketId, threadId), 'PUT', status),
		// Optimistic update: write the new status to the cached threads immediately so
		// the UI reflects Accept/Reject without waiting for the server round-trip or
		// the next poll. Stash previous data so onError can roll back.
		onMutate: async ({ threadId, status }) => {
			await queryClient.cancelQueries({ queryKey: threadsKey });
			const previous = queryClient.getQueryData<ThreadsQueryData>(threadsKey);
			if (previous) {
				queryClient.setQueryData<ThreadsQueryData>(threadsKey, {
					...previous,
					values: previous.values.map(t => (t.id === threadId ? { ...t, status: status as ThreadStatus } : t)),
				});
			}
			return { previous };
		},
		onError: (_err, _vars, context) => {
			if (context?.previous) {
				queryClient.setQueryData<ThreadsQueryData>(threadsKey, context.previous);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: threadsKey });
		},
	});
}

export function useDeepDiveMutation(ticketId: string) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: () => userSession.webServiceHelper.callAsync<string>(SUPPORT_PATHS.threads.deepDive(ticketId), 'POST'),
	});
}

export function useRefineTextMutation(ticketId: string) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({ text, fullReview }: { text: string; fullReview: boolean }) =>
			userSession.webServiceHelper.callAsync<string>(SUPPORT_PATHS.threads.refine(ticketId), 'POST', {
				text,
				fullReview,
			}),
	});
}

interface SendThreadParams {
	threadId: string;
	/** Recipient override; the server falls back to the ticket's contact when omitted. */
	to?: string;
	cc?: string[];
	bcc?: string[];
}

export function useSendThreadMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ threadId, to, cc, bcc }: SendThreadParams) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.threads.send(ticketId, threadId), 'POST', { to, cc, bcc }),
		// The send flips the thread Draft → Sent server-side; refresh so the status isn't
		// stale until the next poll (which is paused while the composer has content).
		onSuccess: () => invalidateTicketThreads(queryClient, ticketId),
	});
}

interface ForwardThreadParams {
	threadId: string;
	to: string[];
	cc?: string[];
	bcc?: string[];
}

export function useForwardThreadMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ threadId, to, cc, bcc }: ForwardThreadParams) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.threads.forward(ticketId, threadId), 'POST', {
				to,
				cc,
				bcc,
			}),
		onSuccess: () => invalidateTicketThreads(queryClient, ticketId),
	});
}
